import { Popover, PopoverPanel } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { useOverlay } from "@Light/components/page/Overlay";
import { useScaffold } from "@Light/scaffold";
import clsx from "clsx";
import {
  AnimatePresence,
  motion,
  type TargetAndTransition,
} from "framer-motion";
import React from "react";

export type BottomSheetProps = {
  open: boolean;
  onClose: () => void;
  children: React.ReactNode;
  className?: string;
  /**
   * Hides the close button. It floats over the top-right corner rather than
   * taking up layout, so children own all of the sheet's spacing — leave room
   * for it when content reaches the top of the sheet.
   */
  hideCloseButton?: boolean;
  /** When false, lets children scroll internally instead of the sheet panel. */
  scrollable?: boolean;
};

const ENTER_TRANSITION = {
  type: "tween",
  duration: 0.25,
  ease: "easeOut",
} as const;
const EXIT_TRANSITION = {
  type: "tween",
  duration: 0.25,
  ease: "easeIn",
} as const;

/**
 * A reusable bottom sheet built on @headlessui/react's Popover. Slides up
 * from the bottom, but from the right if overlay is expanded.
 */
export const BottomSheet: React.FC<BottomSheetProps> = ({
  open,
  onClose,
  children,
  className,
  hideCloseButton = false,
  scrollable = true,
}) => {
  const scaffold = useScaffold();
  const { isExpanded } = useOverlay();

  const offscreen: TargetAndTransition = isExpanded
    ? { x: "100%" }
    : { y: "100%" };
  const onscreen: TargetAndTransition = isExpanded
    ? { x: 0, transition: ENTER_TRANSITION }
    : { y: 0, transition: ENTER_TRANSITION };
  const exitOffscreen: TargetAndTransition = isExpanded
    ? { x: "100%", transition: EXIT_TRANSITION }
    : { y: "100%", transition: EXIT_TRANSITION };

  return (
    <Popover className="contents">
      <AnimatePresence>
        {open && (
          <>
            <motion.div
              className="absolute inset-0 z-50 bg-overlay/50"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.15, ease: "easeOut" }}
              onClick={onClose}
              aria-hidden
            />
            <PopoverPanel
              static
              as={motion.div}
              layout={!scrollable}
              initial={offscreen}
              animate={onscreen}
              exit={exitOffscreen}
              className={clsx(
                "absolute z-50 flex max-h-full flex-col bg-white shadow-xl",
                isExpanded
                  ? "inset-y-0 right-0 w-100 max-w-full"
                  : "inset-x-0 bottom-0 rounded-t-4xl",
                scrollable ? "overflow-y-auto" : "overflow-hidden",
                className,
              )}
            >
              {!hideCloseButton && (
                <button
                  type="button"
                  onClick={onClose}
                  aria-label="Close"
                  className={clsx(
                    "absolute right-3 top-3 z-10 flex items-center justify-center rounded-full p-2 cursor-pointer",
                    "text-fg-quaternary",
                    scaffold.page.colors.hover.highlight,
                  )}
                >
                  <XMarkIcon className="w-5 h-5" />
                </button>
              )}
              {children}
            </PopoverPanel>
          </>
        )}
      </AnimatePresence>
    </Popover>
  );
};
